/** 轻神迹其余:D2 雪 / D3 风 / D5 彩虹 / D6 流星 / D7 共舞。 */
import { Graphics, Sprite } from 'pixi.js';
import { BaseEffect, EffectGroup } from './base';
import { weatherPool, eventPool } from '../engine/ParticlePool';
import { sky } from '../engine/SkyRenderer';
import { world as wind } from '../engine/Ticker';
import { scene } from '../engine/Scene';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { LW, LH } from '../engine/Stage';
import { rand } from '../engine/util';
import { sfx } from '../engine/Sfx';

/** D2 落雪 */
export class SnowEffect extends BaseEffect {
  id = 'snow';
  exclusiveGroup: EffectGroup = 'weather';
  private acc = 0; private popped = false;
  constructor() { super(); this.layerName = 'weatherFX'; }
  onStart() { sky.pushWeather('snow', 10); }
  onUpdate(dt: number) {
    const density = (80 + 120 * this.k) * this.env(4);
    this.acc += density * dt;
    while (this.acc >= 1) { this.acc -= 1; this.spawn(); }
    if (this.t > this.dur && !this.popped) { this.popped = true; sky.popWeather('snow', 12); }
  }
  private spawn() {
    weatherPool?.spawn({
      x: rand(0, LW), y: -20, vx: rand(-20, 20), vy: rand(35, 70), life: 7, scale: 0.02,
      tint: P.snow, alpha: rand(0.5, 0.9), blend: 'add', texture: TEX.glow(), spin: rand(-1, 1), fade: 'none',
      onUpdate: (p, dt) => { p.x += Math.sin(p.age * 1.5) * 30 * dt; if (p.y > scene.island.surfaceYAt(p.x)) p.life = 0; },
    });
  }
  onResolve() { if (!this.popped) { this.popped = true; sky.popWeather('snow', 8); } }
  onDestroy() { if (sky.weather === 'snow') sky.popWeather('snow', 6); }
}

/** D3 起风(修饰一切,自身几乎不可见) */
export class WindEffect extends BaseEffect {
  id = 'wind';
  private streaks: { g: Graphics; age: number; y: number }[] = [];
  private dir = 1; private spawnT = 0;
  onStart() { this.dir = Math.random() < 0.5 ? 1 : -1; wind.setWind(this.dir * 140); sfx.loop('wind', { fadeIn: 2 }); }
  onUpdate(dt: number) {
    // 【修】用户实测"起风基本看不出来":风线密度×3、更粗更亮更长,覆盖更大纵深
    this.spawnT -= dt;
    if (this.spawnT <= 0 && this.t < this.dur) { this.spawnT = rand(0.5, 1.2); this.streak(); }
    for (let i = this.streaks.length - 1; i >= 0; i--) {
      const s = this.streaks[i]; s.age += dt; const p = s.age / 2;
      if (p >= 1) { s.g.destroy(); this.streaks.splice(i, 1); continue; }
      const x = this.dir > 0 ? p * LW * 1.4 - 180 : LW - (p * LW * 1.4 - 180);
      s.g.clear();
      s.g.moveTo(x, s.y);
      s.g.bezierCurveTo(x + this.dir * 140, s.y - 26, x + this.dir * 280, s.y + 14, x + this.dir * 430, s.y - 6);
      s.g.stroke({ width: 2, color: 0xffffff, alpha: (1 - Math.abs(p - 0.5) * 2) * 0.4 });
    }
    if (this.t > this.dur) wind.setWind(0);
  }
  private streak() { const g = new Graphics(); g.blendMode = 'add'; this.g.addChild(g); this.streaks.push({ g, age: 0, y: rand(LH * 0.08, LH * 0.72) }); }
  onResolve() { wind.setWind(0); sfx.stop('wind', 3); }
  onDestroy() { wind.setWind(0); sfx.stop('wind', 2); }
}

/** D5 彩虹 */
export class RainbowEffect extends BaseEffect {
  id = 'rainbow';
  private arc = new Graphics();
  onStart() { this.g.addChild(this.arc); this.arc.blendMode = 'add'; scene.crowd?.gather(LW * 0.4, this.dur); }
  onUpdate() {
    // 【修】彩虹应"以完整形态渐显"(用户定稿),不再从小放大——真实彩虹是
    // 显影出来的,不是吹气球吹出来的。满尺寸固定,只做透明度缓入缓出。
    const fade = Math.min(1, this.t / 2.2) * this.env(1);
    const cx = LW * 0.5, cy = scene.island.surfaceYAt(cx) + 60, r = 560;
    this.arc.clear();
    [0xff5b6b, 0xffa24d, 0xffe14d, 0x66e08a, 0x5bb8ff, 0x9a7bff, 0xd06bff].forEach((c, i) => {
      this.arc.arc(cx, cy, r - i * 9, Math.PI, Math.PI * 2).stroke({ width: 9, color: c, alpha: 0.35 * fade });
    });
  }
}

/** D6 流星(单颗) */
export class MeteorEffect extends BaseEffect {
  id = 'meteor';
  private x = 0; private y = 0; private vx = 0; private vy = 0;
  private head!: Sprite; private trail: Sprite[] = [];
  onStart() {
    // 【修】用户实测"流星不明显":头部×1.7、尾迹更亮更大、速度略降(在屏时间+30%)
    this.dur = 2.4; this.tail = 0.5;
    sfx.play('meteor');
    this.x = rand(LW * 0.6, LW); this.y = rand(0, LH * 0.1);
    const ang = (35 * Math.PI) / 180;
    this.vx = -Math.cos(ang) * 700; this.vy = Math.sin(ang) * 700;
    this.head = new Sprite(TEX.core()); this.head.anchor.set(0.5); this.head.blendMode = 'add'; this.head.tint = 0xeaf2ff; this.head.scale.set(0.075); this.g.addChild(this.head);
    for (let i = 0; i < 12; i++) {
      const t = new Sprite(TEX.glow()); t.anchor.set(0.5); t.blendMode = 'add'; t.tint = P.moon;
      t.scale.set(0.055 - i * 0.0032); t.alpha = 0.85 - i * 0.06; this.g.addChild(t); this.trail.push(t);
    }
  }
  onUpdate(dt: number) {
    for (let i = this.trail.length - 1; i > 0; i--) this.trail[i].position.copyFrom(this.trail[i - 1].position);
    if (this.trail[0]) this.trail[0].position.set(this.x, this.y);
    this.x += this.vx * dt; this.y += this.vy * dt;
    this.head.position.set(this.x, this.y);
    if (Math.random() < 0.5) eventPool?.spawn({ x: this.x, y: this.y, vy: 20, life: 0.5, scale: 0.02, tint: P.moon, alpha: 0.5, blend: 'add', texture: TEX.glow() });
  }
}

/** D7 共舞 */
export class DanceEffect extends BaseEffect {
  id = 'dance';
  private motes: Sprite[] = []; private t2 = 0;
  onStart() {
    scene.crowd?.dance(this.dur);
    for (let i = 0; i < 12; i++) { const s = new Sprite(TEX.glow()); s.anchor.set(0.5); s.blendMode = 'add'; s.tint = P.ember; s.scale.set(0.03); this.g.addChild(s); this.motes.push(s); }
  }
  onUpdate(dt: number) {
    this.t2 += dt;
    const cx = LW * 0.5, cy = scene.island.surfaceYAt(cx) - 10, env = this.env(1);
    this.motes.forEach((s, i) => {
      const a = this.t2 * 0.6 + (i / 12) * 6.28;
      s.position.set(cx + Math.cos(a) * 130, cy + Math.sin(a) * 30);
      s.alpha = env * (0.4 + 0.3 * Math.sin(this.t2 * 2 + i));
    });
  }
}
